import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save, Mail, Send, Info } from "lucide-react";

const empty = { host: "", port: 587, username: "", password: "", from_email: "", from_name: "Magniflex" };

export default function EmailConfigPanel() {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testEmail, setTestEmail] = useState("");

  useEffect(() => {
    api.get("/admin/smtp").then(({ data }) => {
      if (data && data.host) {
        setForm({ ...empty, ...data, password: "" });
      }
    }).catch(() => {});
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.put("/admin/smtp", { ...form, port: Number(form.port) });
      toast.success("Configurazione SMTP salvata");
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const sendTest = async () => {
    if (!testEmail || !/\S+@\S+\.\S+/.test(testEmail)) { toast.error("Inserisci un'email valida per il test"); return; }
    setTesting(true);
    try {
      const { data } = await api.post("/admin/smtp/test", { to_email: testEmail });
      if (data.mocked) toast.warning("SMTP non configurato — email simulata. Salva prima la configurazione.");
      else if (data.sent) toast.success(`Email di test inviata a ${testEmail}`);
      else toast.error(data.error || "Errore invio");
    } catch (e) { toast.error(formatApiError(e)); }
    setTesting(false);
  };

  return (
    <div className="space-y-8" data-testid="email-config">
      <div>
        <h2 className="mgx-display text-4xl text-[#003D5C]">Configurazione Email (SMTP)</h2>
        <p className="text-sm text-[#6B7280] mt-2">Imposta il server SMTP usato per inviare email agli ospiti (coupon) e agli hotel (credenziali portale).</p>
      </div>

      <div className="bg-[#1B3A4B]/5 border border-[#1B3A4B]/20 p-5 max-w-2xl flex gap-3" data-testid="smtp-info-box">
        <Info size={18} className="text-[#1B3A4B] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#1A1C20] leading-relaxed">
          <div className="font-semibold mb-2">Come ottenere i dati SMTP del tuo provider</div>
          <ul className="space-y-1.5 list-disc pl-5 text-[13px]">
            <li><strong>Aruba</strong>: Host <code className="bg-white px-1.5 py-0.5">smtps.aruba.it</code> · Porta <code className="bg-white px-1.5 py-0.5">465</code> (SSL) o <code className="bg-white px-1.5 py-0.5">587</code> (TLS) · Username = la tua email completa · Password = password della casella</li>
            <li><strong>Gmail / Google Workspace</strong>: Host <code className="bg-white px-1.5 py-0.5">smtp.gmail.com</code> · Porta <code className="bg-white px-1.5 py-0.5">587</code> · Username = email · Password = <em>App Password</em> (non la password normale)</li>
            <li><strong>Outlook 365</strong>: Host <code className="bg-white px-1.5 py-0.5">smtp.office365.com</code> · Porta <code className="bg-white px-1.5 py-0.5">587</code></li>
          </ul>
          <div className="text-[12px] mt-3 text-[#6B7280]">
            <strong>Email mittente</strong> è l'indirizzo che apparirà come "Da:" (es. <code>noreply@magniflex.com</code>). <strong>Nome mittente</strong> è ciò che vedono i destinatari (es. "Magniflex Hospitality" — personalizzabile).
          </div>
        </div>
      </div>

      <form onSubmit={save} className="mgx-card max-w-2xl grid md:grid-cols-2 gap-5">
        <div className="md:col-span-2 flex items-center gap-3 text-[#003D5C] mgx-display text-2xl">
          <Mail size={20} /> Server SMTP
        </div>
        <div>
          <label className="mgx-label">Host</label>
          <input required className="mgx-input" placeholder="smtps.aruba.it" value={form.host} onChange={(e) => setForm({ ...form, host: e.target.value })} data-testid="smtp-host" />
        </div>
        <div>
          <label className="mgx-label">Porta</label>
          <input required type="number" className="mgx-input" value={form.port} onChange={(e) => setForm({ ...form, port: e.target.value })} data-testid="smtp-port" />
        </div>
        <div>
          <label className="mgx-label">Username</label>
          <input required className="mgx-input" placeholder="email@tuodominio.it" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="smtp-username" />
        </div>
        <div>
          <label className="mgx-label">Password</label>
          <input required type="password" className="mgx-input" placeholder="••••••••" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="smtp-password" />
        </div>
        <div>
          <label className="mgx-label">Email mittente (visibile a chi riceve)</label>
          <input required type="email" className="mgx-input" placeholder="noreply@magniflex.com" value={form.from_email} onChange={(e) => setForm({ ...form, from_email: e.target.value })} data-testid="smtp-from-email" />
        </div>
        <div>
          <label className="mgx-label">Nome mittente (visibile a chi riceve)</label>
          <input required className="mgx-input" placeholder="Magniflex Hospitality" value={form.from_name} onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="smtp-from-name" />
        </div>
        <div className="md:col-span-2">
          <button type="submit" disabled={loading} className="mgx-btn-primary gap-2" data-testid="smtp-save-btn">
            <Save size={14} /> Salva configurazione
          </button>
        </div>
      </form>

      <div className="mgx-card max-w-2xl">
        <div className="mgx-display text-xl text-[#003D5C] flex items-center gap-2 mb-2">
          <Send size={16} /> Test invio
        </div>
        <p className="text-sm text-[#6B7280] mb-4">Salva prima la configurazione, poi invia un'email di prova al tuo indirizzo per verificare che funzioni.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            placeholder="indirizzo@email.com"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            className="mgx-input flex-1"
            data-testid="smtp-test-email-input"
          />
          <button onClick={sendTest} disabled={testing || !testEmail} className="mgx-btn-secondary gap-2" data-testid="smtp-test-btn">
            <Send size={14} /> {testing ? "Invio..." : "Invia test"}
          </button>
        </div>
      </div>
    </div>
  );
}
